import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import {
    AvailabilityWindowInput,
    DAYS_OF_WEEK,
    hasOverlap,
    isValidWindow,
} from "../../domain/types";

interface AvailabilityEditorProps {
    windows: AvailabilityWindowInput[];
    onChange: (windows: AvailabilityWindowInput[]) => void;
    onSave: (windows: AvailabilityWindowInput[]) => void;
    isSaving?: boolean;
}

const DEFAULT_WINDOW: Omit<AvailabilityWindowInput, "day_of_week"> = {
    start_time: "09:00:00",
    end_time: "13:00:00",
    is_active: true,
};

/** `HH:MM:SS` on the wire, `HH:MM` in the time input. */
const toInput = (value: string) => value.slice(0, 5);
const fromInput = (value: string) => `${value}:00`;

export default function AvailabilityEditor({
    windows,
    onChange,
    onSave,
    isSaving = false,
}: AvailabilityEditorProps) {
    const { t } = useTranslation();
    const [touched, setTouched] = useState(false);

    const invalidWindow = useMemo(
        () => windows.some((window) => !isValidWindow(window)),
        [windows]
    );
    const overlapping = useMemo(() => hasOverlap(windows), [windows]);
    const blocked = invalidWindow || overlapping;

    const update = (index: number, patch: Partial<AvailabilityWindowInput>) => {
        setTouched(true);
        onChange(
            windows.map((window, position) =>
                position === index ? { ...window, ...patch } : window
            )
        );
    };

    const addWindow = (dayOfWeek: number) => {
        setTouched(true);
        onChange([...windows, { ...DEFAULT_WINDOW, day_of_week: dayOfWeek }]);
    };

    const removeWindow = (index: number) => {
        setTouched(true);
        onChange(windows.filter((_, position) => position !== index));
    };

    return (
        <section className="flex flex-col gap-4">
            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                {t("scheduler.settings.availability")}
            </h3>

            {DAYS_OF_WEEK.map((day) => {
                const dayWindows = windows
                    .map((window, index) => ({ window, index }))
                    .filter(({ window }) => window.day_of_week === day);

                return (
                    <div key={day} className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">
                                {t(`scheduler.days.${day}`)}
                            </span>
                            <button
                                type="button"
                                onClick={() => addWindow(day)}
                                aria-label={t("scheduler.settings.addWindow", {
                                    day: t(`scheduler.days.${day}`),
                                })}
                                className="flex items-center gap-1 text-xs text-brand-600"
                            >
                                <Plus size={14} />
                                {t("scheduler.settings.addWindow_short")}
                            </button>
                        </div>

                        {dayWindows.length === 0 && (
                            <p className="text-xs text-gray-500">
                                {t("scheduler.settings.noWindows")}
                            </p>
                        )}

                        {dayWindows.map(({ window, index }) => (
                            <div key={index} className="flex items-center gap-2">
                                <input
                                    type="time"
                                    aria-label={t("scheduler.settings.startTime")}
                                    value={toInput(window.start_time)}
                                    onChange={(event) =>
                                        update(index, {
                                            start_time: fromInput(event.target.value),
                                        })
                                    }
                                    className="rounded-lg border border-gray-300 px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-900"
                                />
                                <span aria-hidden="true">–</span>
                                <input
                                    type="time"
                                    aria-label={t("scheduler.settings.endTime")}
                                    value={toInput(window.end_time)}
                                    onChange={(event) =>
                                        update(index, {
                                            end_time: fromInput(event.target.value),
                                        })
                                    }
                                    className="rounded-lg border border-gray-300 px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-900"
                                />
                                <label className="flex items-center gap-1 text-xs">
                                    <input
                                        type="checkbox"
                                        checked={window.is_active}
                                        onChange={(event) =>
                                            update(index, { is_active: event.target.checked })
                                        }
                                    />
                                    {t("scheduler.settings.active")}
                                </label>
                                <button
                                    type="button"
                                    onClick={() => removeWindow(index)}
                                    aria-label={t("scheduler.settings.removeWindow")}
                                    className="text-red-500"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                );
            })}

            {touched && invalidWindow && (
                <p role="alert" className="text-sm text-red-600">
                    {t("scheduler.settings.errorEndBeforeStart")}
                </p>
            )}
            {touched && !invalidWindow && overlapping && (
                <p role="alert" className="text-sm text-red-600">
                    {t("scheduler.settings.errorOverlap")}
                </p>
            )}

            {/* Timezone and duration save themselves; availability needs this
                button, so an edited-but-unsaved schedule must say so. */}
            {touched && !blocked && (
                <p className="text-xs text-amber-600">
                    {t("scheduler.settings.unsavedAvailability")}
                </p>
            )}

            <button
                type="button"
                disabled={blocked || isSaving}
                onClick={() => {
                    onSave(windows);
                    setTouched(false);
                }}
                className="self-start rounded-lg bg-brand-600 px-4 py-2 text-sm text-white disabled:opacity-50"
            >
                {isSaving
                    ? t("scheduler.settings.saving")
                    : t("scheduler.settings.saveAvailability")}
            </button>
        </section>
    );
}
